import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import i18n from 'i18n';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { DefaultDeviceManagementManager } from 'core/deviceManagement/DeviceManagementManager';
import {
  DeviceChannel,
  DeviceUnitLevel,
  DeviceUnitRecord
} from 'core/deviceManagement/DeviceManagement';
import { useCallAPI } from 'hooks/useCallAPI';
import { ALL_SPACE_STATUSES } from 'enum/SpaceStatus';
import {
  buildHealthStateOptions,
  DEVICE_FILTER_OPTION_BAG_KEYS,
  DeviceFilterField,
  DeviceFilterOptionsState,
  DeviceManagementFilter,
  DEFAULT_FILTER,
  EMPTY_FILTER,
  REGION_OPTION_BAG_KEY
} from './deviceFilters';
import { DEVICE_LIST_DATE_FORMAT, DeviceListColumn } from './deviceListColumns';
import { DeviceImportList } from './deviceImportList';

const deviceManagementManager = new DefaultDeviceManagementManager();
const adRequestSourceManager = new DefaultAdRequestSourceManager();

/** 沒有名單時送出去的空陣列。共用同一個實體，才不會每次 render 都看成換了條件。 */
const NO_STORE_NUMS: string[] = [];

/**
 * 名單比對那一次查詢一次要回幾筆。
 *
 * 要回答「哪幾個店號查不到」，手上就得有整份名單的比對結果 —— 這是一句否定的
 * 斷言，拿清單的某一頁去推會把第二頁之後的每一筆都冠上「查無此店號」。
 * 超過這個筆數就什麼都不宣稱（見 useDeviceManagementModel 裡的 matchedUnits）。
 */
export const IMPORT_MATCH_QUERY_SIZE = 500;

export const useDeviceManagementModel = () => {
  const { loading, callAPIs } = useCallAPI();
  const [channels, setChannels] = useState<DeviceChannel[]>([]);
  const [salesChannel, setSalesChannel] = useState<number>();
  const [spaceAttributeOptions, setSpaceAttributeOptions] = useState<{
    [key: string]: SelectOptions[];
  }>({});
  // 這個渠道的選項到底取回來了沒。沒有這個旗標的話，「還在取」「取回來就是空的」
  // 與「取失敗」在畫面上長得一模一樣，而三者對使用者的意思不同：第一種要等，
  // 第二種是這個渠道真的沒有那種值，第三種只能重整。選項來源是版位那一支（AsiaMax），
  // 與清單不是同一個資料源，所以清單有資料不代表選項就會有。
  const [filterOptionsState, setFilterOptionsState] =
    useState<DeviceFilterOptionsState>(DeviceFilterOptionsState.LOADING);
  const [filter, setFilter] = useState<DeviceManagementFilter>(DEFAULT_FILTER);
  const [importList, setImportList] = useState<DeviceImportList>();
  // 整份名單比對回來的那幾列。名單模式的「需確認」是對整份名單下的結論，
  // 所以留的是整批列而不是只留店號：縣市與店名的校驗也要對同一份母體做，
  // 拿清單當下那一頁去校驗，數字會隨著翻頁跳動。
  const [matchedUnits, setMatchedUnits] = useState<DeviceUnitRecord[]>();

  useEffect(() => {
    // 走機台管理自己的渠道端點，而不是共用的銷售渠道清單：哪些渠道在這一頁
    // 看得到（例如電子紙要不要排除）由後端的單一入口判定，而那個判定式
    // 前端沒有辦法可靠地複製一份 —— 比對渠道型別字串是已知會出錯的做法。
    // 這一支同時帶回渠道的投遞單位，清單才知道自己是門市層還是機台層。
    //
    // 拿到渠道之後刻意不替使用者選一個：這一頁的每一支查詢都以渠道為母體，
    // 自動選第一個等於替他決定了要看哪一條產線，而那一條多半不是他要的，
    // 畫面卻已經先跑完一輪查詢。改由他自己選，沒選之前什麼都不查。
    callAPIs(
      [() => deviceManagementManager.getChannels()],
      (deviceChannels: DeviceChannel[]) => {
        setChannels(deviceChannels);
      }
    );
  }, [callAPIs]);

  const salesChannelOptions = useMemo(
    () =>
      channels.map((channel) => ({
        label: channel.channelName,
        value: channel.channelId
      })),
    [channels]
  );

  const channelDeliveryUnit = useMemo(
    () =>
      channels.find((channel) => channel.channelId === salesChannel)
        ?.deliveryUnit,
    [channels, salesChannel]
  );

  /** 這個渠道的投遞點位是門市還是機台，接在渠道下拉後面當提示。 */
  const channelDeliveryUnitLabel = useMemo(() => {
    if (channelDeliveryUnit === undefined) {
      return undefined;
    }
    // 用渠道投遞單位既有的那一組文案，不另立一份：同一個概念在渠道設定與這一頁
    // 叫不同名字的話，使用者對不起來，而兩份文案也會各自漂移。
    return channelDeliveryUnit === DeviceUnitLevel.DEVICE
      ? i18n.t('deliveryUnit.device')
      : i18n.t('deliveryUnit.store');
  }, [channelDeliveryUnit]);

  useEffect(() => {
    if (salesChannel === undefined) {
      return;
    }
    // 四個篩選條件的選項都是「這個渠道底下有哪些值」，換渠道就整組重取。
    // 沿用廣告活動版位選擇器的同一支，選項來源才會一致；但只指名這一頁用得到的三組，
    // 因為那支是「一組屬性一次請求」，不指名會連店號與機台特徵也一起取回來丟掉。
    //
    // 五種版位狀態全帶：不帶這個條件時後端只涵蓋啟用中的版位，而這一頁的清單
    // 本來就會列出其他狀態的版位 —— 那些版位看得到，卻沒有辦法在篩選裡挑出來。
    //
    // 失敗要自己接住：預設的錯誤處理只跳一個 toast，狀態會留在「還在取」，
    // 而這支 effect 只在換渠道時才會再跑一次 —— 三個下拉會一直寫著「載入中」，
    // toast 消失之後畫面上就沒有任何線索了。toast 照舊跳，那是唯一寫得出原因的地方。
    setFilterOptionsState(DeviceFilterOptionsState.LOADING);
    callAPIs(
      [
        () =>
          adRequestSourceManager.getOutdoorSpaceAttributes(
            salesChannel,
            false,
            ALL_SPACE_STATUSES,
            DEVICE_FILTER_OPTION_BAG_KEYS
          )
      ],
      (attributes: { [key: string]: SelectOptions[] }) => {
        setSpaceAttributeOptions(attributes);
        setFilterOptionsState(DeviceFilterOptionsState.LOADED);
      },
      (error) => {
        setFilterOptionsState(DeviceFilterOptionsState.FAILED);
        error instanceof Error && toast.error(error.message);
      }
    );
  }, [salesChannel, callAPIs]);

  // 名單模式要能說出「這幾筆查無此店號」，而那是一句否定的斷言：手上得有整份
  // 名單的比對結果才講得出口。清單那一支是分頁的，用它那一頁去推，第二頁之後的
  // 每一筆都會被冠上查無。所以這裡另外問一次，只帶名單、不帶其他條件（名單是母體，
  // 其他條件是在名單裡面再篩，混進來會把「被篩掉」講成「查不到」）。
  //
  // 一次問不完時給 undefined 而不是給一份不完整的清單：不知道的時候不要亂講。
  useEffect(() => {
    if (!importList || salesChannel === undefined) {
      setMatchedUnits(undefined);
      return;
    }
    setMatchedUnits(undefined);
    callAPIs(
      [
        () =>
          deviceManagementManager.getUnits({
            channelId: salesChannel,
            page: 0,
            size: IMPORT_MATCH_QUERY_SIZE,
            sortField: DeviceListColumn.STORE_NUM,
            sortOrder: 'asc',
            search: '',
            filter: {},
            storeNums: importList.storeNums
          })
      ],
      (result) => {
        setMatchedUnits(
          result.pagination.totalCount > result.records.length
            ? undefined
            : result.records
        );
      }
    );
  }, [importList, salesChannel, callAPIs]);

  const healthStateOptions = useMemo(() => buildHealthStateOptions(), []);

  const filterOptions = useMemo(
    () => ({
      [DeviceFilterField.PLACE_CATEGORY]:
        spaceAttributeOptions[DeviceFilterField.PLACE_CATEGORY] ?? [],
      [DeviceFilterField.ADMINISTRATIVE_AREA]:
        spaceAttributeOptions[DeviceFilterField.ADMINISTRATIVE_AREA] ?? [],
      [DeviceFilterField.REGION]:
        spaceAttributeOptions[REGION_OPTION_BAG_KEY] ?? [],
      [DeviceFilterField.HEALTH_STATE]: healthStateOptions
    }),
    [spaceAttributeOptions, healthStateOptions]
  );

  const onSalesChannelChange = useCallback((value: number) => {
    setSalesChannel(value);
    // 上一個渠道的選項留著會讓下拉短暫顯示別的渠道的值。
    setSpaceAttributeOptions({});
    // 已選的值屬於上一個渠道，留著會送出新渠道裡根本不存在的條件。
    // 回到進頁時那一份而不是全空：狀態的預設排除不分渠道，換一次渠道就讓
    // 停止服務的機台回到清單最前面，等於這個預設只對第一個渠道有效。
    setFilter(DEFAULT_FILTER);
    // 名單是店號，換渠道之後那些店號未必在新渠道底下，留著會變成一份查不到東西的名單。
    setImportList(undefined);
  }, []);

  const onFilterChange = useCallback(
    (field: DeviceFilterField, values: string[]) => {
      setFilter((prev) => ({ ...prev, [field]: values }));
    },
    []
  );

  // 一次清掉四個條件。逐個下拉按清除的話，每清一個就重查一次清單。
  const onClearFilter = useCallback(() => {
    setFilter(EMPTY_FILTER);
  }, []);

  const storeNums = useMemo(
    () => importList?.storeNums ?? NO_STORE_NUMS,
    [importList]
  );

  const onExitListMode = useCallback(() => setImportList(undefined), []);

  // 版位同步每 10 分鐘一輪，所以這個時間正常情況一定是今天，只寫時分就夠、也與設計稿一致。
  // 但同步停掉時它會停在前幾天，而「最後同步 10:42」看起來與剛更新過一模一樣 ——
  // 那正是最需要被看見的狀況，所以不是今天就連日期一起寫。
  const buildLastSyncedLabel = useCallback((lastSyncedAt?: string) => {
    if (!lastSyncedAt) {
      return undefined;
    }
    const synced = moment(lastSyncedAt);
    return i18n.t('deviceManagement.labels.lastSynced', {
      time: synced.isSame(moment(), 'day')
        ? synced.format('HH:mm')
        : synced.format(DEVICE_LIST_DATE_FORMAT)
    });
  }, []);

  return {
    loading,
    salesChannelOptions,
    salesChannel,
    channelDeliveryUnit,
    channelDeliveryUnitLabel,
    onSalesChannelChange,
    filter,
    onFilterChange,
    onClearFilter,
    filterOptions,
    filterOptionsState,
    importList,
    matchedUnits,
    storeNums,
    setImportList,
    onExitListMode,
    buildLastSyncedLabel
  };
};

export type DeviceManagementModel = ReturnType<typeof useDeviceManagementModel>;
